import { Button, DialogActions, DialogContent } from '@mui/material';
import { useCallback, useEffect } from 'react';
import { useForm, FormProvider, FieldValues, DefaultValues } from 'react-hook-form';
import { ZodType } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useModal } from '../../contexts/ModalContext';
import useMutateData from '../../hooks/useMutateData';
import { QUERY_KEYS } from '../../constants';
import { StyleObj } from '../../@types';
import { Marketplace } from '../../@types/api';

const styles: StyleObj = {
  content: { px: 0 },
  actions: {
    '&.MuiDialogActions-root': {
      px: 0,
      pb: 0,
    },
  },
};

type ConfigTabTemplateProps<TFormData extends FieldValues> = {
  dataKey: keyof Marketplace;
  schema: ZodType<TFormData, unknown>;
  defaultValues: DefaultValues<TFormData>;
  transformData?: (data: TFormData) => Partial<TFormData>;
  resolveInitialValues?: (data: Partial<TFormData> | undefined) => TFormData;
  children: React.ReactNode;
};

const ConfigTabTemplate = <TFormData extends FieldValues>({
  dataKey,
  schema,
  defaultValues,
  transformData,
  resolveInitialValues,
  children,
}: ConfigTabTemplateProps<TFormData>) => {
  const { item, closeModal } = useModal<Marketplace>();
  const { updateData, isLoading } = useMutateData('bo/marketplace', [QUERY_KEYS.marketplace], 'crm');

  const formContext = useForm<TFormData>({
    resolver: zodResolver(schema as ZodType<TFormData, TFormData>),
    defaultValues,
  });
  const {
    reset,
    handleSubmit,
    formState: { isDirty },
  } = formContext;

  const getInitialValues = useCallback(
    (data: Partial<TFormData> | undefined) => {
      if (resolveInitialValues) {
        return resolveInitialValues(data);
      }

      return (data as TFormData | undefined) ?? defaultValues;
    },
    [defaultValues, resolveInitialValues]
  );

  useEffect(() => {
    reset(getInitialValues(item?.[dataKey] as Partial<TFormData> | undefined));
  }, [dataKey, getInitialValues, item, reset]);

  const handleClose = () => {
    reset(getInitialValues(item?.[dataKey] as Partial<TFormData> | undefined));
    closeModal?.();
  };

  const onFormSubmit = (data: TFormData) => {
    const payload = transformData ? transformData(data) : data;

    if (item) updateData(item.id, { [dataKey]: payload }, handleClose);
  };

  return (
    <FormProvider {...formContext}>
      <DialogContent sx={styles.content}>{children}</DialogContent>
      <DialogActions sx={styles.actions}>
        <Button variant='outlined' onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant='contained'
          onClick={handleSubmit(onFormSubmit)}
          disabled={!isDirty || isLoading}
          aria-busy={isLoading}
        >
          {isLoading ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </FormProvider>
  );
};

export default ConfigTabTemplate;
